import type { CallHeardMarker } from '@compass/client/types/call';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { markersToHeardMap } from './heardMarkers';

type HeardMark = { at: number; offsetMs?: number };
export type HeardMap = Record<string, HeardMark>;

export interface HeardFields {
  heardKeys: Set<string>;
  isHeard: (key: string) => boolean;
  heardAt: (key: string) => number | undefined;
  markHeard: (key: string) => void;
  unmarkHeard: (key: string) => void;
  toggleHeard: (key: string) => void;
  /** Drop all heard marks for this call and remove its localStorage entry. */
  clear: () => void;
}

export interface UseHeardFieldsOptions {
  /** Monotonic ms (performance.now()) of the call's connected event; enables heard_offset_ms. */
  connectedMonotonicMs?: number;
  /** Authoritative base from the persisted call record (FR4). */
  initialMarkers?: Record<string, CallHeardMarker>;
  /** Called only on USER-driven changes (not hydration) with the next map, for debounced persist. */
  onUserChange?: (map: HeardMap) => void;
}

export const heardStorageKey = (callId: string | undefined | null): string | null =>
  callId ? `humanCall:heard:${callId}` : null;

export function loadHeard(storageKey: string | null): HeardMap {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as HeardMap) : {};
  } catch {
    return {};
  }
}

export function saveHeard(storageKey: string | null, map: HeardMap): void {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    /* storage full/disabled — heard marks are best-effort */
  }
}

/**
 * Remove a call's heard-marks entry from localStorage (best-effort). The durable
 * copy lives on the call record (heard_markers); this only clears the local cache.
 */
export function clearHeard(callId: string | null | undefined): void {
  const storageKey = heardStorageKey(callId);
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    /* storage disabled — nothing to clean up */
  }
}

export function markHeardInMap(
  map: HeardMap,
  key: string,
  at: number,
  offsetMs?: number,
): HeardMap {
  if (map[key]) return map; // idempotent — keep the first timestamp
  return { ...map, [key]: offsetMs != null ? { at, offsetMs } : { at } };
}

export function unmarkHeardInMap(map: HeardMap, key: string): HeardMap {
  if (!map[key]) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

export function toggleHeardInMap(
  map: HeardMap,
  key: string,
  at: number,
  offsetMs?: number,
): HeardMap {
  return map[key] ? unmarkHeardInMap(map, key) : markHeardInMap(map, key, at, offsetMs);
}

/**
 * Per-call "heard" marks. Stores ONLY field keys + timestamps + an optional monotonic
 * offset — NEVER field values (no PHI). The backend heard_markers map is the authoritative
 * base on hydrate; localStorage is the fallback when the record has no markers yet (FR4).
 */
export function useHeardFields(
  callId: string | undefined | null,
  opts?: UseHeardFieldsOptions,
): HeardFields {
  const storageKey = heardStorageKey(callId);
  const [map, setMap] = useState<HeardMap>(() => loadHeard(storageKey));

  // Refs keep callbacks referentially stable while always seeing the latest values.
  const callIdRef = useRef(callId);
  callIdRef.current = callId;
  const mapRef = useRef(map);
  mapRef.current = map;
  const connectedRef = useRef(opts?.connectedMonotonicMs);
  connectedRef.current = opts?.connectedMonotonicMs;
  const onUserChangeRef = useRef(opts?.onUserChange);
  onUserChangeRef.current = opts?.onUserChange;

  // Once the user makes a local mark this call, backend hydration must not clobber it.
  const dirtyRef = useRef(false);

  // Re-seed from localStorage when the call changes; reset the backend-hydration guard.
  // Sentinel (not undefined) so the `=== callId` guard below is correct even when
  // callId is itself undefined — '__unhydrated__' can never equal a 24-hex ObjectID.
  const hydratedForRef = useRef<string | null | undefined>('__unhydrated__');
  useEffect(() => {
    setMap(loadHeard(storageKey));
    hydratedForRef.current = '__unhydrated__';
    dirtyRef.current = false;
  }, [storageKey]);

  // Hydrate from the authoritative backend markers once they arrive (FR4): backend is the
  // base; localStorage stands only when the record has no markers yet.
  const initialMarkers = opts?.initialMarkers;
  useEffect(() => {
    // Skip once hydrated for this call, or once the user has made local marks — a late
    // refetch carrying server markers must not overwrite newer local edits (FR4).
    if (hydratedForRef.current === callId || dirtyRef.current) return;
    if (initialMarkers && Object.keys(initialMarkers).length > 0) {
      setMap(markersToHeardMap(initialMarkers));
      hydratedForRef.current = callId;
    }
  }, [callId, initialMarkers]);

  // Write-through cache.
  useEffect(() => {
    saveHeard(storageKey, map);
  }, [storageKey, map]);

  const applyUserChange = useCallback((next: HeardMap) => {
    dirtyRef.current = true;
    mapRef.current = next;
    setMap(next);
    onUserChangeRef.current?.(next);
  }, []);

  const markHeard = useCallback(
    (key: string) => {
      const offsetMs =
        connectedRef.current != null
          ? Math.round(performance.now() - connectedRef.current)
          : undefined;
      applyUserChange(markHeardInMap(mapRef.current, key, Date.now(), offsetMs));
    },
    [applyUserChange],
  );
  const unmarkHeard = useCallback(
    (key: string) => applyUserChange(unmarkHeardInMap(mapRef.current, key)),
    [applyUserChange],
  );
  const toggleHeard = useCallback(
    (key: string) => {
      const offsetMs =
        connectedRef.current != null
          ? Math.round(performance.now() - connectedRef.current)
          : undefined;
      applyUserChange(toggleHeardInMap(mapRef.current, key, Date.now(), offsetMs));
    },
    [applyUserChange],
  );
  const clear = useCallback(() => {
    clearHeard(callIdRef.current);
    mapRef.current = {};
    setMap({});
  }, []);

  // Memoized so its identity is stable across renders (only changes when `map` does).
  // Consumers feed heardKeys into expensive memos (buildScriptModel/buildFieldBoard);
  // a fresh Set every render would bust those on every poll/re-render — major lag.
  const heardKeys = useMemo(() => new Set(Object.keys(map)), [map]);
  const isHeard = useCallback((key: string) => Boolean(map[key]), [map]);
  const heardAt = useCallback((key: string) => map[key]?.at, [map]);

  // Stable object identity (changes only when map changes) so consumers that depend on
  // the whole `heard` object (e.g. memoized callbacks) don't churn every render.
  return useMemo(
    () => ({ heardKeys, isHeard, heardAt, markHeard, unmarkHeard, toggleHeard, clear }),
    [heardKeys, isHeard, heardAt, markHeard, unmarkHeard, toggleHeard, clear],
  );
}
